import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import * as Icons from 'lucide-react';
import Announcements from '../components/Announcements';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import { getImageUrl } from '../lib/utils';

const Home = () => {
  const [news, setNews] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [features, setFeatures] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Fetch news
    fetch(`${BACKEND_URL}/api/news/`)
      .then(res => res.json())
      .then(data => setNews(data.slice(0, 4)))
      .catch(err => console.error('Error fetching news:', err));

    // Fetch programs
    fetch(`${BACKEND_URL}/api/programs/`)
      .then(res => res.json())
      .then(data => setPrograms(data.slice(0, 6)))
      .catch(err => console.error('Error fetching programs:', err));

    // Fetch settings
    fetch(`${BACKEND_URL}/api/settings/`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(err => console.error('Error fetching settings:', err));

    // Fetch features
    fetch(`${BACKEND_URL}/api/features/`)
      .then(res => res.json())
      .then(data => setFeatures(data))
      .catch(err => console.error('Error fetching features:', err));
  }, [BACKEND_URL]);

  const stats = settings?.home_settings ? [
    { number: settings.home_settings.stats_programs || '10+', label: settings.home_settings.stats_programs_label_ar || 'برنامج أكاديمي' },
    { number: settings.home_settings.stats_faculty || '50+', label: settings.home_settings.stats_faculty_label_ar || 'عضو هيئة تدريس' },
    { number: settings.home_settings.stats_students || '1000+', label: settings.home_settings.stats_students_label_ar || 'طالب وطالبة' },
    { number: settings.home_settings.stats_agreements || '5+', label: settings.home_settings.stats_agreements_label_ar || 'اتفاقيات دولية' }
  ] : [
    { number: '10+', label: 'برنامج أكاديمي' },
    { number: '50+', label: 'عضو هيئة تدريس' },
    { number: '1000+', label: 'طالب وطالبة' },
    { number: '5+', label: 'اتفاقيات دولية' }
  ];

  // Extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const heroBackgroundType = settings?.home_settings?.hero_background_type || 'image';
  const heroBackgroundImage = settings?.home_settings?.hero_background_image;
  const heroBackgroundYoutube = settings?.home_settings?.hero_background_youtube;
  const heroBackgroundVideo = settings?.home_settings?.hero_background_video;

  return (
    <div className="min-h-screen">
      <SEO 
        title="الصفحة الرئيسية"
        description="جامعة ابن النفيس للعلوم الطبية - أول جامعة أهلية متخصصة في العلوم الطبية في اليمن. نقدم برامج أكاديمية متميزة في طب الأسنان، الصيدلة، العلاج الطبيعي والعلوم الطبية."
        keywords="جامعة ابن النفيس, جامعة طبية اليمن, طب أسنان, صيدلة, علاج طبيعي, تعليم طبي, جامعة صنعاء"
        url="https://uni-content-fixer.preview.emergentagent.com/"
      />
      
      {settings && (
        <StructuredData 
          type="organization"
          data={{
            name_ar: settings.site_name_ar,
            name_en: settings.site_name_en,
            logo: settings.site_logo,
            description_ar: settings.about_ar,
            address_ar: settings.contact_settings?.address_ar,
            phone: settings.contact_settings?.phone,
            email: settings.contact_settings?.email,
            facebook: settings.contact_settings?.facebook,
            twitter: settings.contact_settings?.twitter,
            instagram: settings.contact_settings?.instagram,
            youtube: settings.contact_settings?.youtube,
            linkedin: settings.contact_settings?.linkedin
          }}
        />
      )}
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background - Dynamic based on settings */}
        {heroBackgroundType === 'image' && heroBackgroundImage ? (
          // Image Background
          <div className="absolute inset-0">
            <img 
              src={getImageUrl(heroBackgroundImage)} 
              alt="Hero Background" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0F388E]/70"></div>
          </div>
        ) : heroBackgroundType === 'youtube' && heroBackgroundYoutube && getYouTubeVideoId(heroBackgroundYoutube) ? (
          // YouTube Video Background
          <div className="absolute inset-0">
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(heroBackgroundYoutube)}?autoplay=1&mute=1&loop=1&playlist=${getYouTubeVideoId(heroBackgroundYoutube)}&controls=0&showinfo=0&rel=0&modestbranding=1`}
              className="absolute top-1/2 left-1/2 w-[100vw] h-[100vh] -translate-x-1/2 -translate-y-1/2"
              style={{ minWidth: '100%', minHeight: '100%', width: '177.77vh', height: '56.25vw' }}
              frameBorder="0"
              allow="autoplay; encrypted-media"
              title="Hero Background Video"
            ></iframe>
            <div className="absolute inset-0 bg-[#0F388E]/60"></div>
          </div>
        ) : heroBackgroundType === 'video' && heroBackgroundVideo ? (
          // Uploaded Video Background
          <div className="absolute inset-0">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover"
            >
              <source src={heroBackgroundVideo} type="video/mp4" />
              <source src={heroBackgroundVideo} type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-[#0F388E]/60"></div>
          </div>
        ) : (
          // Default gradient background
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F388E] via-[#1a4fa8] to-[#0F388E] hero-pattern">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyMDEsMTYwLDY0LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fadeInUp">
            {/* University Decorative Name Image */}
            <div className="mb-8 mx-auto max-w-4xl dark:shadow-none dark:drop-shadow-none">
              <img 
                src="https://customer-assets.emergentagent.com/job_meduni-admin/artifacts/nixxa8kv_%D8%A7%D8%B3%D9%85%20%D8%A7%D9%84%D8%AC%D8%A7%D9%85%D8%B9%D8%A9%20%D9%85%D8%B2%D8%AE%D8%B1%D9%811111.png"
                alt="جامعة ابن النفيس للعلوم الطبية"
                className="w-full h-auto object-contain drop-shadow-2xl dark:drop-shadow-none dark:shadow-none dark:filter-none"
                style={{ maxHeight: '250px' }}
              />
            </div>

            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
              أول جامعة أهلية متخصصة في العلوم الطبية في اليمن
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/register">
                <Button size="lg" className="bg-[#C9A040] hover:bg-[#C9A040]/90 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                  سجل الآن
                  <ArrowLeft className="mr-2" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0F388E] px-8 py-6 text-lg transition-all">
                  استعرض البرامج
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                <div className="text-4xl font-bold text-[#C9A040] mb-2">{stat.number}</div>
                <div className="text-white/90 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 right-1/2 translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-[#071326] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F388E] dark:text-white mb-4">
              لماذا جامعة ابن النفيس؟
            </h2>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const IconComponent = Icons[feature.icon];
              const CardWrapper = feature.link_url ? 'a' : 'div';
              const cardProps = feature.link_url ? {
                href: feature.link_url,
                target: feature.link_url.startsWith('http') ? '_blank' : '_self',
                rel: feature.link_url.startsWith('http') ? 'noopener noreferrer' : undefined
              } : {};
              
              return (
                <CardWrapper key={feature.id} {...cardProps} className={feature.link_url ? 'block' : ''}>
                  <Card className="group hover:shadow-2xl transition-all duration-300 border-t-4 border-t-[#C9A040] hover:scale-105 h-full cursor-pointer bg-white dark:bg-[#0E2243] border border-gray-100 dark:border-gray-800">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-[#0F388E] dark:bg-[#1E3A8A] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#C9A040] dark:group-hover:bg-[#C9A040] transition-colors">
                        {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                      </div>
                      <h3 className="text-xl font-bold text-[#0F388E] dark:text-white group-hover:text-[#C9A040] dark:group-hover:text-[#FACC15] mb-3 transition-colors">{feature.title_ar}</h3>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{feature.description_ar}</p>
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-20 bg-gray-50 dark:bg-[#0B1A30] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F388E] dark:text-white mb-4">
              برامجنا الأكاديمية
            </h2>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
              نقدم مجموعة متنوعة من البرامج الطبية المتخصصة
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {programs.map((program) => (
              <Card key={program.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white dark:bg-[#0E2243] border border-gray-100 dark:border-gray-800">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={getImageUrl(program.image, 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800')}
                    alt={program.title_ar}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 right-4 bg-[#C9A040] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {program.duration}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-3 group-hover:text-[#C9A040] dark:group-hover:text-[#FACC15] transition-colors">
                    {program.title_ar}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">{program.description_ar}</p>
                  <Link to={`/programs/${program.id}`}>
                    <Button variant="link" className="text-[#C9A040] hover:text-[#0F388E] dark:hover:text-white p-0">
                      اقرأ المزيد
                      <ArrowLeft className="mr-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/programs">
              <Button size="lg" className="bg-[#0F388E] hover:bg-[#0F388E]/90 dark:bg-[#123985] dark:hover:bg-[#1E4EAA] text-white">
                عرض جميع البرامج
                <ArrowLeft className="mr-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-white dark:bg-[#071326] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F388E] dark:text-white mb-4">
              آخر الأخبار
            </h2>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {news.map((item) => (
              <Card key={item.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white dark:bg-[#0E2243] border border-gray-100 dark:border-gray-800">
                <div className="flex flex-col md:flex-row">
                  <div className="relative w-full md:w-1/3 h-48 md:h-auto overflow-hidden">
                    <img
                      src={getImageUrl(item.image, 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800')}
                      alt={item.title_ar}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="flex-1 p-6">
                    <div className="text-sm text-[#C9A040] font-semibold mb-2">{item.category}</div>
                    <h3 className="text-lg font-bold text-[#0F388E] dark:text-white mb-2 group-hover:text-[#C9A040] dark:group-hover:text-[#FACC15] transition-colors">
                      {item.title_ar}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                      {item.excerpt_ar || item.content_ar?.replace(/<[^>]*>?/gm, '').substring(0, 120)}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{new Date(item.published_date).toLocaleDateString('ar-EG')}</span>
                      <Link to={`/news/${item.id}`}>
                        <Button variant="link" className="text-[#C9A040] hover:text-[#0F388E] dark:hover:text-white p-0 text-sm">
                          اقراء المزيد
                          <ArrowLeft className="mr-1 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/news">
              <Button size="lg" variant="outline" className="border-2 border-[#0F388E] dark:border-[#C9A040] text-[#0F388E] dark:text-[#C9A040] hover:bg-[#0F388E] dark:hover:bg-[#C9A040] hover:text-white dark:hover:text-white">
                عرض جميع الأخبار
                <ArrowLeft className="mr-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Announcements Section - After News */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white mb-4">
              📢 الإعلانات
            </h2>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
          </div>
          <Announcements location="home" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] "></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ابدأ رحلتك الطبية معنا
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            انضم إلى ألاف الطلاب الذين يحققون أحلامهم في مجال العلوم الطبية
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#C9A040] hover:bg-[#C9A040]/90 text-white px-8 py-6 text-lg shadow-xl">
                سجل الآن
                <ArrowLeft className="mr-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0F388E] px-8 py-6 text-lg">
                اتصل بنا
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;